import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@4zen/api-client';
import { Button, Field, Input, useBranding } from '@4zen/ui';
import { tenantSlugFromLocation, usePublicBranding } from '@/lib/branding';
import { useAuth } from '@/stores/auth';

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgSlug, setOrgSlug] = useState(() => tenantSlugFromLocation() ?? '');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Typing the organisation brands the screen as you go; two characters is
  // early enough to feel instant and late enough not to query every letter.
  const { data: branding } = usePublicBranding(orgSlug.trim().length >= 2 ? orgSlug.trim() : null);
  useBranding(branding);

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login({
        email,
        password,
        org_slug: orgSlug || undefined,
        totp_code: totp || undefined,
      });
      navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'totp_required') {
        setNeedsTotp(true);
        setError('Введіть код із застосунку автентифікації');
      } else {
        setError(caught instanceof ApiError ? caught.message : 'Не вдалося увійти');
      }
    } finally {
      setBusy(false);
    }
  }

  const productName = branding?.product_name ?? '4ZEN CRM';

  return (
    <div className="grid h-full place-items-center bg-bg px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt="" className="h-10 w-10 rounded object-contain" />
          ) : (
            <span className="grid h-10 w-10 place-items-center rounded bg-accent text-base font-bold text-accent-contrast">
              {productName.trim().slice(0, 2).toUpperCase()}
            </span>
          )}
          <div>
            <p className="text-lg font-semibold">{productName}</p>
            {branding?.hide_vendor_branding ? null : (
              <p className="text-xs text-content-subtle">Фокус на одній задачі за раз</p>
            )}
          </div>
        </div>

        <form onSubmit={submit} className="zen-panel space-y-4 p-6">
          <Field label="Робоча пошта" htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>

          <Field label="Пароль" htmlFor="password">
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>

          <Field
            label="Організація"
            htmlFor="org"
            hint="Потрібна лише якщо ця пошта працює в кількох організаціях"
          >
            <Input
              id="org"
              value={orgSlug}
              placeholder="напр. demo"
              onChange={(event) => setOrgSlug(event.target.value)}
            />
          </Field>

          {needsTotp ? (
            <Field label="Код двофакторної автентифікації" htmlFor="totp">
              <Input
                id="totp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={totp}
                onChange={(event) => setTotp(event.target.value)}
              />
            </Field>
          ) : null}

          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" className="w-full" loading={busy}>
            Увійти
          </Button>
        </form>
      </div>
    </div>
  );
}
